import type { CSSProperties } from 'react'
import dayjs from 'dayjs'
import { useMutation } from '@tanstack/react-query'

import { cn } from '@jotta/utils/css'

import type { CollectionRef } from '../../../api/useCollection'
import { useSelectionStore } from '../../../store/PhotoContext'
import { PhotoCheckbox } from '../../PhotoCheckbox/PhotoCheckbox'
import { MediaThumb } from '../MediaThumb'

import type {
  TimelineDividerV2,
  TimelineHeaderV2,
  TimelinePhotoRowV2,
  TimelineRowDataV2,
} from './groupV2'
import { usePhotoIdsForDay } from './useDaySelection'

export function formatVideoDuration(videoDurationSeconds: number) {
  const secondsFormatted = (videoDurationSeconds % 60)
    .toString()
    .padStart(2, '0')
  const minutes = Math.floor(videoDurationSeconds / 60) % 60
  const minutesFormatted = minutes.toString().padStart(2, '0')

  if (videoDurationSeconds < 3600) {
    return `${minutesFormatted}:${secondsFormatted}`
  }

  const hoursFormatted = Math.floor(videoDurationSeconds / 3600)
    .toString()
    .padStart(2, '0')

  return `${hoursFormatted}:${minutesFormatted}:${secondsFormatted}`
}

export function PhotoRowV2Heading({
  collectionRef,
  header,
  style,
}: {
  collectionRef: CollectionRef
  header: TimelineHeaderV2
  style?: CSSProperties
}) {
  const { day } = header
  const date = dayjs.utc(day)
  const selection = useSelectionStore()

  const {
    data: photos,
    isLoading,
    refetch,
  } = usePhotoIdsForDay(collectionRef, date, {
    enabled: selection.hasDaySelection(date),
  })

  const isSelected = !!photos && photos.every(photo => selection.has(photo.id))

  const { mutate: onToggleSelect } = useMutation({
    mutationFn: async (selected: boolean) => {
      const togglePhotos = photos || (await refetch()).data || []
      if (selected) {
        selection.add(...togglePhotos)
      } else {
        selection.delete(...togglePhotos.map(photo => photo.id))
      }
    },
  })

  return (
    <div className="pb-2 pt-6" style={style}>
      <div
        onClick={() => onToggleSelect(!isSelected)}
        className={cn(
          'btn inline-flex items-center justify-between gap-10 rounded-xl border border-outline-light bg-surface-container-lowest px-3 py-2',
          {
            'border-surface-container-low bg-surface-container-lowest':
              isSelected,
          },
        )}
        data-testid="photoRowHeaderWrapper"
      >
        <div className="flex items-baseline gap-2">
          <header className="h3">{date.format('D MMMM')}</header>
          <span className="body-sm-ln">{date.format('YYYY')}</span>
        </div>

        <PhotoCheckbox
          loading={isLoading}
          isSelected={isSelected}
          onToggleSelect={selected => onToggleSelect(selected)}
        />
      </div>
    </div>
  )
}
export function PhotoRowV2Divider({
  row,
  style,
}: {
  row: TimelineDividerV2
  style: CSSProperties
}) {
  return (
    <div className="flex flex-col justify-around" style={style}>
      <hr />
    </div>
  )
}

export function PhotoRowV2({
  row,
  next,
  style,
  showOwnerAvatar = false,
  showSimilarSearch = false,
}: {
  row: TimelinePhotoRowV2
  next: TimelineRowDataV2 | undefined
  style: CSSProperties
  showOwnerAvatar?: boolean
  showSimilarSearch?: boolean
}) {
  const { elements, fractions, firstInDay } = row
  const lastIndex = elements.length - 1
  const lastInDay = !next || next?.content !== 'row'
  const needBottomRightRounding =
    lastInDay || (next?.content === 'row' && next.incomplete)
  return (
    <div className="relative w-full" style={style}>
      <div
        className="grid h-full gap-1"
        style={{
          gridTemplateColumns: fractions.map(f => `${f}fr`).join(' '),
        }}
      >
        {elements.map((photo, index) => {
          const {
            idDeprecated,
            expiringSmallThumbUrl,
            fileName,
            videoDurationSeconds,
            thumbHash,
          } = photo
          const firstInRow = index === 0
          const lastInRow = index === lastIndex
          return (
            <MediaThumb
              className={cn({
                'rounded-tl-xl': firstInDay && firstInRow,
                'rounded-tr-xl': firstInDay && lastInRow,
                'rounded-bl-xl': lastInDay && firstInRow,
                'rounded-br-xl': needBottomRightRounding && lastInRow,
              })}
              key={idDeprecated}
              src={expiringSmallThumbUrl}
              photo={photo}
              filename={fileName}
              duration={
                videoDurationSeconds
                  ? formatVideoDuration(videoDurationSeconds)
                  : undefined
              }
              playIcon={Boolean(videoDurationSeconds)}
              showOwnerAvatar={showOwnerAvatar}
              showSimilarSearch={showSimilarSearch}
              thumbHash={thumbHash}
            />
          )
        })}
      </div>
    </div>
  )
}
